import { Box, Button, FormControl, InputLabel, MenuItem, Select, TextField } from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { EmptyState } from '../EmptyState';
import { ErrorState } from '../ErrorState';
import { LoadingState } from '../LoadingState';
import { PageControlsBar } from '../PageControlsBar';
import { PageHeader } from '../PageHeader';
import { useDashboardAccountScope } from './voterAccountScope';
import type { VoterFetchImpl } from './voterApi';
import { defaultVotersListState, readVotersListState, toStoredVotersListState } from './voterNavigation';
import {
  ALL_VOTER_STATES_LABEL,
  ALL_VOTER_STATES_VALUE,
  DEFAULT_VOTER_PAGE_SIZE,
  NO_VOTERS_AVAILABLE,
  NO_VOTERS_MATCH,
  VOTER_PAGE_SIZE_OPTIONS,
  VOTER_SEARCH_DEBOUNCE_MS,
  VOTER_SEARCH_MAX_LENGTH,
  VOTER_SEARCH_OVERLENGTH_MESSAGE,
  isVoterPageSize,
  isVoterStateFilterValue,
  voterRegistrationStateOptions,
  voterStateFilterLabel,
} from './voterPresentation';
import { DEFAULT_VOTER_PAGE } from './voterListState';
import { VoterTable } from './VoterTable';
import { useVoterList } from './useVoterList';
import { useVoterRegistrationStates } from './useVoterRegistrationStates';

const visuallyHiddenSx = {
  position: 'absolute',
  width: 1,
  height: 1,
  p: 0,
  m: -1,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
  border: 0,
} as const;

export type VotersPageProps = {
  fetchImpl?: VoterFetchImpl;
};

// Empty string is All states. It is the default when history has no restored choice.
// Loaded registration states must not replace that default with the first or last value.
export const VotersPage = ({ fetchImpl }: VotersPageProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const accountScope = useDashboardAccountScope();
  const restoredList = readVotersListState(location.state, accountScope);
  const restored = restoredList ?? defaultVotersListState();
  const [seenScope, setSeenScope] = useState(accountScope);
  const [searchInput, setSearchInput] = useState(restored.search);
  const [committedSearch, setCommittedSearch] = useState(restored.search);
  const [stateFilter, setStateFilter] = useState(restored.state);
  const [page, setPage] = useState(restored.page);
  const [rowsPerPage, setRowsPerPage] = useState(restored.rowsPerPage);
  const [validation, setValidation] = useState<string | null>(null);
  const debounceRef = useRef<number | null>(null);
  const committedSearchRef = useRef(committedSearch);
  committedSearchRef.current = committedSearch;

  if (seenScope !== accountScope) {
    setSeenScope(accountScope);
    setSearchInput('');
    setCommittedSearch('');
    setStateFilter(ALL_VOTER_STATES_VALUE);
    setPage(DEFAULT_VOTER_PAGE);
    setRowsPerPage(DEFAULT_VOTER_PAGE_SIZE);
    setValidation(null);
    committedSearchRef.current = '';
  }

  const scopeMatches = seenScope === accountScope;
  const registrationStates = useVoterRegistrationStates({ fetchImpl });

  const activeSearch = scopeMatches ? committedSearch : '';
  const activeState = scopeMatches ? stateFilter : ALL_VOTER_STATES_VALUE;
  const activePage = scopeMatches ? page : DEFAULT_VOTER_PAGE;
  const activeRowsPerPage = scopeMatches ? rowsPerPage : DEFAULT_VOTER_PAGE_SIZE;
  const list = useVoterList({
    search: activeSearch,
    state: activeState,
    page: activePage,
    rowsPerPage: activeRowsPerPage,
    fetchImpl,
    enabled: scopeMatches,
  });
  const stateOptions = voterRegistrationStateOptions(registrationStates.data ?? [], activeState);
  const hasActiveFilter = activeSearch.length > 0 || activeState.length > 0;

  const clearDebounce = (): void => {
    if (debounceRef.current === null) return;
    window.clearTimeout(debounceRef.current);
    debounceRef.current = null;
  };

  const commitSearch = (raw: string): void => {
    clearDebounce();
    if (raw.length > VOTER_SEARCH_MAX_LENGTH) {
      setValidation(VOTER_SEARCH_OVERLENGTH_MESSAGE);
      return;
    }

    const trimmed = raw.trim();
    setValidation(null);
    setSearchInput(trimmed);
    if (committedSearchRef.current === trimmed) return;

    committedSearchRef.current = trimmed;
    setCommittedSearch(trimmed);
    setPage(DEFAULT_VOTER_PAGE);
  };

  useEffect(() => {
    if (searchInput.length > VOTER_SEARCH_MAX_LENGTH) {
      setValidation(VOTER_SEARCH_OVERLENGTH_MESSAGE);
      return undefined;
    }

    setValidation(current => (current === null ? current : null));
    const trimmed = searchInput.trim();
    if (trimmed === committedSearchRef.current && searchInput === trimmed) return undefined;

    const timer = window.setTimeout(() => commitSearch(searchInput), VOTER_SEARCH_DEBOUNCE_MS);
    debounceRef.current = timer;
    return () => {
      window.clearTimeout(timer);
      if (debounceRef.current === timer) debounceRef.current = null;
    };
  }, [searchInput]);

  useEffect(() => {
    if (!scopeMatches) return undefined;

    const nextState = {
      votersList: toStoredVotersListState(
        { search: activeSearch, state: activeState, page: activePage, rowsPerPage: activeRowsPerPage },
        accountScope,
      ),
    };
    const current = readVotersListState(location.state, accountScope);
    if (
      current !== null &&
      current.search === nextState.votersList.search &&
      current.state === nextState.votersList.state &&
      current.page === nextState.votersList.page &&
      current.rowsPerPage === nextState.votersList.rowsPerPage
    ) {
      return undefined;
    }

    navigate('/voters', { replace: true, state: nextState });
    return undefined;
  }, [
    accountScope,
    activePage,
    activeRowsPerPage,
    activeSearch,
    activeState,
    location.state,
    navigate,
    scopeMatches,
  ]);

  useEffect(() => {
    if (list.data === null || list.loading) return;
    const lastPage = Math.max(0, Math.ceil(list.data.total / activeRowsPerPage) - 1);
    if (list.data.total > 0 && activePage > lastPage) setPage(lastPage);
  }, [activePage, activeRowsPerPage, list.data, list.loading]);

  const handleSubmit = (event: { preventDefault: () => void }): void => {
    event.preventDefault();
    commitSearch(searchInput);
  };

  const handleClear = (): void => {
    clearDebounce();
    setValidation(null);
    setSearchInput('');
    if (committedSearchRef.current !== '') {
      committedSearchRef.current = '';
      setCommittedSearch('');
    }
    setPage(DEFAULT_VOTER_PAGE);
  };

  const chooseState = (next: string): void => {
    if (!isVoterStateFilterValue(next)) return;
    setStateFilter(next);
    setPage(DEFAULT_VOTER_PAGE);
  };

  const handleStateChange = (event: SelectChangeEvent): void => {
    chooseState(event.target.value);
  };

  const handleRowsPerPageChange = (nextRowsPerPage: number): void => {
    if (!isVoterPageSize(nextRowsPerPage) || !VOTER_PAGE_SIZE_OPTIONS.includes(nextRowsPerPage)) return;
    setRowsPerPage(nextRowsPerPage);
    setPage(DEFAULT_VOTER_PAGE);
  };

  const announcement = list.loading
    ? 'Loading voters.'
    : list.error !== null
      ? list.error
      : list.data === null || list.data.items.length === 0
        ? hasActiveFilter
          ? NO_VOTERS_MATCH
          : NO_VOTERS_AVAILABLE
        : `${list.data.total} voters.`;

  const linkState = {
    votersList: toStoredVotersListState(
      { search: activeSearch, state: activeState, page: activePage, rowsPerPage: activeRowsPerPage },
      accountScope,
    ),
  };

  return (
    <>
      <PageHeader title="Voters" />
      <Box component="div" role="status" aria-live="polite" aria-atomic="true" sx={visuallyHiddenSx}>
        {announcement}
      </Box>
      <PageControlsBar label="Search">
        <Box
          component="form"
          role="search"
          aria-label="Search voters"
          noValidate
          onSubmit={handleSubmit}
          sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'flex-start', width: { xs: '100%', sm: 'auto' } }}
        >
          <TextField
            label="Search voters"
            value={searchInput}
            onChange={event => setSearchInput(event.target.value)}
            onKeyDown={event => {
              if (event.key !== 'Enter') return;
              event.preventDefault();
              commitSearch(searchInput);
            }}
            error={validation !== null}
            helperText={validation ?? undefined}
            size="small"
            sx={{ width: { xs: '100%', sm: 360 } }}
          />
          <FormControl size="small" sx={{ minWidth: 160, width: { xs: '100%', sm: 180 } }}>
            <InputLabel id="voter-registration-state-label" shrink>
              State
            </InputLabel>
            <Select
              labelId="voter-registration-state-label"
              label="State"
              value={activeState}
              displayEmpty
              renderValue={selected => voterStateFilterLabel(selected)}
              onChange={handleStateChange}
            >
              {/* Select suppresses onChange when the clicked value is already selected. */}
              <MenuItem value={ALL_VOTER_STATES_VALUE} onClick={() => chooseState(ALL_VOTER_STATES_VALUE)}>
                {ALL_VOTER_STATES_LABEL}
              </MenuItem>
              {stateOptions.map(option => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button type="submit" tabIndex={-1} sx={visuallyHiddenSx}>
            Search voters
          </Button>
          <Button type="button" onClick={handleClear}>
            Clear search
          </Button>
        </Box>
      </PageControlsBar>
      {!registrationStates.loading && registrationStates.error !== null ? (
        <ErrorState
          message={registrationStates.error}
          onRetry={registrationStates.refetch}
          retryName="Retry registration states"
        />
      ) : null}
      {list.loading ? <LoadingState message="Loading voters." /> : null}
      {!list.loading && list.error !== null ? (
        <ErrorState message={list.error} onRetry={list.refetch} retryName="Retry voters" />
      ) : null}
      {!list.loading && list.error === null && (list.data === null || list.data.items.length === 0) ? (
        <EmptyState title={hasActiveFilter ? NO_VOTERS_MATCH : NO_VOTERS_AVAILABLE} />
      ) : null}
      {!list.loading && list.error === null && list.data !== null && list.data.items.length > 0 ? (
        <VoterTable
          items={list.data.items}
          total={list.data.total}
          page={activePage}
          rowsPerPage={activeRowsPerPage}
          linkState={linkState}
          onPageChange={setPage}
          onRowsPerPageChange={handleRowsPerPageChange}
        />
      ) : null}
    </>
  );
};
